// src/services/templateVersionService.js
//
// Read/write side of a sourcex_templates doc's version history — stored as
// its own subcollection (sourcex_templates/{id}/versions/{versionId}), not a
// field on the template document. See the comment above MAX_TEMPLATE_VERSIONS
// in templateSchema.js for why: an embedded array shares the template's own
// 1 MiB Firestore document budget with every past snapshot combined, which is
// exactly what broke template approval once a template had a few full-content
// snapshots behind it.
//
// migrateEmbeddedVersions() below handles a template that still carries the
// OLD embedded `versionHistory` array (from before this file existed) — it
// moves that data into the subcollection and clears the field, shrinking the
// template document back down. Call it before pushing a new version so a
// template that's currently blocked on the 1 MiB limit gets fixed by the very
// action that was failing.

import {
  collection,
  deleteField,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { getSourcexTemplatesPath, getTemplateVersionsPath } from "./firestorePaths";
import { MAX_TEMPLATE_VERSIONS } from "./templateSchema";

/**
 * How many times ONE visit has been approved.
 *
 * Every approval writes a `kind: "approved"` snapshot for that visit (see
 * markApproved), so the count of those IS the visit's revision number — which
 * is what prefills the exported document's Version. Counted per visit, not per
 * template, because approval is per visit: Screening being on its third
 * revision says nothing about Visit 3.
 *
 * Returns 0 rather than throwing if the history can't be read — the export
 * then falls back to its usual default instead of failing.
 *
 * @param {string} templateId
 * @param {string} eventId
 * @returns {Promise<number>}
 */
export const countVisitApprovals = async (templateId, eventId) => {
  if (!templateId || !eventId) return 0;
  try {
    const snap = await getDocs(collection(db, getTemplateVersionsPath(templateId)));
    return snap.docs.filter((d) => {
      const v = d.data();
      return v?.kind === "approved" && v?.eventId === eventId;
    }).length;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[templateVersionService] approval count failed:", err);
    return 0;
  }
};

/** Every version for a template, newest first — same order the old embedded
 * array kept, so VersionHistoryModal doesn't need to know which it's reading. */
export const listTemplateVersions = async (templateId) => {
  const snap = await getDocs(
    query(collection(db, getTemplateVersionsPath(templateId)), orderBy("createdAt", "desc"))
  );
  return snap.docs.map((d) => d.data());
};

/**
 * Write one new version snapshot and prune anything past MAX_TEMPLATE_VERSIONS
 * (oldest first). `entry` comes from templateSchema.js's makeTemplateVersion()
 * — already PHI-stripped and shaped.
 * @param {string} templateId
 * @param {object} entry
 */
export const pushTemplateVersion = async (templateId, entry) => {
  const versionsPath = getTemplateVersionsPath(templateId);
  await setDoc(doc(db, versionsPath, entry.id), entry);

  const snap = await getDocs(
    query(collection(db, versionsPath), orderBy("createdAt", "desc"))
  );

  // Pruned PER VISIT, not across the template as a whole. A version now covers
  // one visit (see makeTemplateVersion), and a single cap over all of them
  // means a fortnight of work on Screening silently deletes the entire history
  // of every other visit — the histories are independent, so their limits are
  // too. Whole-template snapshots (the older ones, and rebuilds) share one
  // bucket of their own.
  const buckets = new Map();
  snap.docs.forEach((d) => {
    const key = String(d.data()?.eventId || "");
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(d);
  });

  const excess = [...buckets.values()].flatMap((docs) =>
    docs.slice(MAX_TEMPLATE_VERSIONS)
  );
  if (excess.length) {
    const batch = writeBatch(db);
    excess.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
};

/**
 * One-time migration for a template still carrying the legacy embedded
 * `versionHistory` array: moves every entry into the subcollection, then
 * clears the field on the template doc so it shrinks back under the 1 MiB
 * limit. No-op (and no write at all) when there's nothing to migrate — safe
 * to call unconditionally before any version-touching write.
 * @param {string} templateId
 * @param {Array|undefined} embedded  the template doc's current `versionHistory`
 */
export const migrateEmbeddedVersions = async (templateId, embedded) => {
  const list = Array.isArray(embedded) ? embedded : [];
  if (!list.length) return;

  const versionsPath = getTemplateVersionsPath(templateId);
  const batch = writeBatch(db);
  list.slice(0, MAX_TEMPLATE_VERSIONS).forEach((v) => {
    if (v && v.id) batch.set(doc(db, versionsPath, v.id), v);
  });
  batch.update(doc(db, getSourcexTemplatesPath(), templateId), {
    versionHistory: deleteField(),
  });
  await batch.commit();
};
